import { Box, Button, Sheet, Stack, Typography } from "@mui/joy";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { taskApi } from "api";
import { useMutation } from "@tanstack/react-query";
import { useRecoilState, useRecoilValue } from "recoil";
import { userAtom } from "recoil/userAtom";
import { deviceAtom } from "recoil/deviceAtom";
import { ProgramContentType } from "data/BlockComponent";
import PlainTextarea, { PlainTextareaHandle } from "./textarea/PlainTextarea";
import { ElementType, FeedbackType } from "data/CellComponent";
import * as _ from "lodash";
import { debounce } from "lodash";
import { useTranslation } from "react-i18next";
import { theme } from "styles/theme";
import useOpenTask from "hooks/useOpenTask";
import useSubscriptionStatus from "hooks/useSubscriptionStatus";
import { sendMessage } from "utils/webview";
import useIsInappWebview from "hooks/useIsInappWebview";
import OptionalModal from "../../../common/OptionalModal";
import CustomModal from "../../../common/CustomModal";
import { invalidTextareaIndexAtom } from "recoil/invalidTextareaIndexAtom";

export default function FeedbackCell({
  taskKey,
  savedFeedback,
  blockIndex,
  setData,
  lineIndex,
  cellIndex,
  editorKey,
}: {
  taskKey: string;
  savedFeedback?: string;
  blockIndex: number;
  setData: React.Dispatch<
    React.SetStateAction<ProgramContentType[] | undefined>
  >;
  lineIndex: number;
  cellIndex: number;
  editorKey?: string;
}) {
  const userState = useRecoilValue(userAtom);
  const isCoach = userState?.role !== "patient";
  const { isSubscribing } = useSubscriptionStatus();

  const [feedback, setFeedback] = useState<string>(savedFeedback || "");
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  const plainTextareaRef = useRef<PlainTextareaHandle>(null);

  // 밖에서 값이 바뀌면(자동 채우기) 따라간다 — 입력 중이거나 내가 저장한 값이면 그대로.
  // 따라가지 않으면 아래 디바운스 저장이 화면의 옛 값("")으로 덮어쓴다
  useEffect(() => {
    const value = savedFeedback || "";
    if (isFocused || value === feedback) return;
    setFeedback(value);
    if (value) setIsSubmitted(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedFeedback]);

  const [deviceState, setDeviceState] = useRecoilState(deviceAtom);
  const [isPushModalOpen, setIsPushModalOpen] = useState<boolean>(false);
  const [hasShownPushModal, setHasShownPushModal] = useState<boolean>(false);

  const [invalidTextareaIndexList, setInvalidTextareaIndexList] =
    useRecoilState(invalidTextareaIndexAtom);
  const indexKey = `${blockIndex}-${lineIndex}-${cellIndex}`;
  const isInvalidTextarea = invalidTextareaIndexList.includes(indexKey);

  const hadInvalidBorder = useRef(isInvalidTextarea);

  useEffect(() => {
    if (isInvalidTextarea) {
      hadInvalidBorder.current = true;
    }
  }, [isInvalidTextarea]);

  const openPayment = useOpenTask({ taskKey: "payment" });
  const isInappWebview = useIsInappWebview();

  const { t: t_common } = useTranslation("translation", {
    keyPrefix: "task.activity.common",
  });

  const submitFeedbackQueryFn = useCallback(
    (text: string) => taskApi.submitFeedback(taskKey, text),
    [taskKey]
  );

  // 전송이 확정된 뒤: 입력값 flush → "전달되었습니다." 하이라이트 → (권한 없으면) 알림 요청
  const { mutate: submitFeedback } = useMutation(submitFeedbackQueryFn, {
    onSuccess: (_, text) => {
      plainTextareaRef.current?.clear(); // 화면 입력값까지 확실히 비움 (포커스 중에도)
      setFeedback("");
      handleChange("", true, text);
      setIsSubmitted(true);
    },
  });

  const updateInvalidIndexList = useCallback(
    (val: string) => {
      setInvalidTextareaIndexList((prev) => {
        if (!hadInvalidBorder.current) return prev;
        const trimmed = (val || "").trim();
        const exists = prev.includes(indexKey);
        if (trimmed.length > 0) {
          return exists ? prev : [...prev, indexKey];
        }
        return exists ? prev.filter((idx) => idx !== indexKey) : prev;
      });
    },
    [indexKey, setInvalidTextareaIndexList]
  );

  const handleChangeTemp = (value: string) => {
    setFeedback(value);
    updateInvalidIndexList(value);
    if (value.length > 0) {
      setIsSubmitted(false);
    }
  };

  const handleChange = (
    value: string,
    autoOpenNext?: boolean,
    sentFeedback?: string
  ) => {
    setData((data) => {
      if (data && data[blockIndex] && data[blockIndex].lines) {
        const originalCurrentContent = (data[blockIndex].lines[lineIndex][
          cellIndex
        ].content || {}) as ElementType;
        const originalValue = originalCurrentContent.value || "";

        // 값이 같아도 autoOpenNext 요청은 반영 (디바운스 저장 전에 전송이 완료된 경우 누락 방지)
        if (!(originalValue === value) || autoOpenNext) {
          const data_temp = _.cloneDeep(data);
          const currentContent =
            data_temp[blockIndex].lines[lineIndex][cellIndex].content || {};

          data_temp[blockIndex].lines[lineIndex][cellIndex].content = {
            ...currentContent,
            value: value,
            htmlString: `<p>${value}</p>`,
          };
          if (autoOpenNext) {
            data_temp[blockIndex].autoOpenNext = true;
          }
          // 교육용 모의 내담자: 보낸 소감은 칸이 비워지므로 따로 남겨 마치기 알림에 싣는다 (docs/user-sim-agent.md 5장)
          if (sentFeedback && userState?.isSimulated) {
            const cell = data_temp[blockIndex].lines[lineIndex][cellIndex];
            cell.content = {
              ...cell.content,
              sentFeedbacks: [
                ...((cell.content as FeedbackType).sentFeedbacks || []),
                sentFeedback,
              ],
            };
          }

          return data_temp;
        }
        return data;
      }
      return data;
    });
    updateInvalidIndexList(value);
  };

  const debouncedSaveText = debounce(handleChange, 500);

  useEffect(() => {
    debouncedSaveText(feedback);

    // 컴포넌트가 언마운트될 때 디바운스 함수를 취소합니다.
    return () => debouncedSaveText.cancel();
  }, [feedback, debouncedSaveText]);

  // 웹뷰에서 푸시 권한 상태를 모르면 앱에 조회 요청 (전송 시점 전에 상태 확보)
  useEffect(() => {
    if (
      isInappWebview &&
      !isCoach &&
      deviceState.pushNotificationStatus === undefined
    ) {
      sendMessage(JSON.stringify({ event: "get_pushNotification_status" }));
    }
  }, [isInappWebview, isCoach, deviceState.pushNotificationStatus]);

  // 소감 전송 성공 시, 푸시 권한이 허용 상태가 아니면 알림 켜기 안내 (마운트당 최대 1회)
  useEffect(() => {
    if (!isSubmitted || hasShownPushModal) return;
    if (!isInappWebview || isCoach) return;
    const status = deviceState.pushNotificationStatus;
    if (status === undefined || status === "granted") return;
    setHasShownPushModal(true);
    setIsPushModalOpen(true);
  }, [
    isSubmitted,
    hasShownPushModal,
    isInappWebview,
    isCoach,
    deviceState.pushNotificationStatus,
  ]);

  return (
    <Stack spacing={"6px"} data-feedback-cell>
      <Stack direction={"row"} spacing={1}>
        <Box sx={{ flex: 1, minWidth: "0px" }}>
          {/* minWidth:0 빼면 모바일에서 넘침 */}
          <Sheet
            id={`textarea-${indexKey}`}
            sx={{
              width: "100%",
              height: "100%",
              backgroundColor: "white",
              px: "10px",
              py: "12px",
              borderRadius: "6px",
              borderWidth: "1.4px",
              borderStyle: "solid",
              borderColor: isInvalidTextarea
                ? theme.vars.palette.Border.HighlightWithAlert
                : isFocused
                  ? theme.vars.palette.Border.Highlight
                  : theme.vars.palette.Border.Weak,
              position: "relative",
            }}
          >
            <PlainTextarea
              ref={plainTextareaRef}
              placeholder={t_common("소감 placeholder")}
              initialValue={savedFeedback}
              handleChange={handleChangeTemp}
              isFocused={isFocused}
              setIsFocused={setIsFocused}
              editorKey={editorKey}
            />
          </Sheet>
        </Box>

        <Button
          disabled={isCoach || feedback.trim().length === 0}
          sx={{ px: "10px" }}
          onClick={() => {
            if (!isSubscribing) {
              setIsModalOpen(true);
            } else {
              submitFeedback(feedback);
            }
          }}
        >
          <Box
            sx={{
              width: "24px",
              height: "24px", //크기 지정 안하면 resize 어쩌구 에러
            }}
          >
            <span className="material-symbols-rounded">send</span>
          </Box>
        </Button>
      </Stack>
      {isSubmitted && (
        <Typography
          variant="Caption"
          textColor={theme.vars.palette.Content.Highlight}
          sx={{ fontWeight: 700, px: "2px" }}
        >
          {"전달되었습니다."}
        </Typography>
      )}
      <OptionalModal
        open={isModalOpen}
        setOpen={setIsModalOpen}
        title={"결제하고 메시지를 보내보세요"}
        subtitle={"코치 선생님과 함께 프로그램을 진행해보세요."}
        content={
          <Box
            sx={{
              mt: 1,
              display: "flex",
              gap: 1,
              // flexDirection: { xs: "row", sm: "center" },
              justifyContent: "space-between",
            }}
          >
            <Button
              disabled={false}
              variant="soft"
              size="lg"
              color={"primary"}
              onClick={(e) => {
                e.stopPropagation();
                setFeedback("");
                setIsModalOpen(false);
              }}
              sx={{
                width: "100%",
              }}
              loading={false}
            >
              {"보내지 않기"}
            </Button>
            <Button
              disabled={false}
              variant="solid"
              size="lg"
              color={"primary"}
              onClick={(e) => {
                e.stopPropagation();
                if (isInappWebview) {
                  sendMessage(JSON.stringify({ event: "open_payment" })); // 앱이 웹뷰 열도록
                } else {
                  openPayment(); // 페이지 이동
                }
              }}
              sx={{
                width: "100%",
              }}
              loading={false}
            >
              {"더 알아보기"}
            </Button>
          </Box>
        }
      />
      <CustomModal
        open={isPushModalOpen}
        onClose={() => setIsPushModalOpen(false)}
        title={"알림을 켜주세요"}
        subtitle={"코치 선생님의 채팅 메시지를 푸시 알림으로 알려드려요."}
        actions={[
          {
            label: "알림 허용하기",
            variant: "Solid",
            color: "primary",
            onClick: () => {
              sendMessage(
                JSON.stringify({
                  event: "request_pushNotification",
                  params: {
                    enableGoToSetting: true,
                    disableRequest: false,
                  },
                })
              );
              setDeviceState((prev) => ({
                ...prev,
                pushNotificationStatus: "not set",
              }));
              setIsPushModalOpen(false);
            },
          },
          {
            label: "나중에 하기",
            variant: "Plain",
            onClick: () => setIsPushModalOpen(false),
          },
        ]}
      />
    </Stack>
  );
}
